import { isDatabaseUnavailableError } from '../../shared/localDb';
import { setSentryOrganizationTag } from '../../shared/sentry/sentryUser';
import { supabase } from '../../shared/supabase/supabaseClient';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { getOfflineAccountSnapshot } from '../../shell/session/offlineAccountCache';
import { canRunOperationalBackgroundWork } from './operationalBackgroundWork';
import { getOrganizationEntitlementsCacheIfFresh, fetchMyOrganizationEntitlements } from '../feature-flags/planEntitlementsService';
import type { PlanEntitlementsRow } from '../feature-flags/domain/planEntitlements';

const sessionOrgByUserId = new Map<string, string>();

/** Limpia caché en memoria (p. ej. logout). */
export function clearSessionOrganizationCache(): void {
  sessionOrgByUserId.clear();
  setSentryOrganizationTag(null);
}

/**
 * Actualiza la org en sesión conocida por red/entitlements sin depender de SQLite
 * (p. ej. PlanEntitlementsProvider tras fetch o lectura de snapshot local).
 */
export function primeSessionOrganizationFromEntitlements(userId: string, entitlements: PlanEntitlementsRow | null): void {
  const oid = entitlements?.organization_id?.trim();
  if (oid) {
    sessionOrgByUserId.set(userId, oid);
    setSentryOrganizationTag(oid);
  }
}

/** Tras onboarding o RPC que devuelve organization_id sin fila de entitlements. */
export function primeSessionOrganizationId(userId: string, organizationId: string): void {
  const oid = organizationId.trim();
  if (oid) {
    sessionOrgByUserId.set(userId, oid);
    setSentryOrganizationTag(oid);
  }
}

/** Org ya resuelta en memoria/caché (sin ir a red). */
export function getKnownSessionOrganizationId(): string | null {
  const uid = useAuthStore.getState().session?.user?.id;
  if (!uid) return null;

  const cached = sessionOrgByUserId.get(uid)?.trim();
  if (cached) return cached;

  const entCached = getOrganizationEntitlementsCacheIfFresh();
  return entCached?.organization_id?.trim() ?? null;
}

/** Organización de la sesión (SQLite offline + caché + RPC entitlements). */
export async function getSessionOrganizationId(): Promise<string | null> {
  const uid = useAuthStore.getState().session?.user?.id;
  if (!uid) return null;

  let fromSqlite: string | null = null;
  try {
    const snap = await getOfflineAccountSnapshot(uid);
    fromSqlite = snap?.entitlements?.organization_id?.trim() || null;
  } catch (e) {
    if (!isDatabaseUnavailableError(e)) throw e;
  }
  if (fromSqlite) {
    sessionOrgByUserId.set(uid, fromSqlite);
    return fromSqlite;
  }

  const cached = sessionOrgByUserId.get(uid)?.trim();
  if (cached) return cached;

  const entCached = getOrganizationEntitlementsCacheIfFresh();
  const entOid = entCached?.organization_id?.trim();
  if (entOid) {
    sessionOrgByUserId.set(uid, entOid);
    return entOid;
  }

  const row = await fetchMyOrganizationEntitlements();
  const oid = row?.organization_id?.trim() ?? null;
  if (oid) sessionOrgByUserId.set(uid, oid);
  return oid;
}

export async function requireSessionOrganizationId(): Promise<string> {
  const id = await getSessionOrganizationId();
  if (!id) {
    throw new Error('No hay organización en sesión. Volvé a iniciar sesión.');
  }
  return id;
}

/** Producto «Varios» sembrado por org en servidor (`is_misc`). Supabase es source of truth. */
export async function getMiscProductIdForOrganization(organizationId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('products')
    .select('id')
    .eq('organization_id', organizationId)
    .eq('is_misc', true)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.warn('[sessionOrganization] misc product remote', error.message);
    return null;
  }
  return data?.id ?? null;
}
